import type { CookieConsentConfig, CookieValue } from 'vanilla-cookieconsent';
import * as Cookies from 'vanilla-cookieconsent';
import createEnglishTranslation from './Localization/en.ts';
import createPolishTranslation from './Localization/pl.ts';
import createCzechTranslation from './Localization/cs.ts';
import { GoogleTagManager, GoogleTagManagerInjectionKey } from '../Analytics/GoogleTagManager';
import { Router, RouterInjectionKey } from '@othercommerce/foundation';
import { type InjectionKey, type App, inject } from 'vue';
import { resolveFromApp } from '@othercommerce/domain';

export const CookieConsentInjectionKey = Symbol('CookieConsent') as InjectionKey<CookieConsent>;

export class CookieConsent {
  private readonly locale: string;
  private readonly router: Router;
  private readonly gtm: GoogleTagManager;

  constructor(locale: string, gtm: GoogleTagManager, router: Router) {
    this.locale = locale;
    this.gtm = gtm;
    this.router = router;
  }

  public run() {
    Cookies.run(this.buildDefaultSettings());
  }

  private buildDefaultSettings(): CookieConsentConfig {
    const self = this;
    const router = this.router;
    const locale = this.locale;

    return {

      language: {
        default: locale,
        translations: {
          en: createEnglishTranslation(router),
          pl: createPolishTranslation(router),
          cs: createCzechTranslation(router),
        },
      },

      mode: 'opt-in',
      autoShow: true,
      disablePageInteraction: true,

      guiOptions: {
        consentModal: {
          layout: 'box wide',
          position: 'middle center',
        },
        preferencesModal: {
          layout: 'box',
        },
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
        },
        targeting: {
          enabled: false,
        },
        functionality: {
          enabled: false,
        },
      },

      onConsent({ cookie }) {
        self.pushUpdatedConsent(cookie);
      },

      onChange({ cookie }) {
        self.pushUpdatedConsent(cookie);
      },
    };
  }

  private pushUpdatedConsent(cookie: CookieValue) {
    this.gtm.push('consent', 'update', {
      'ad_storage': cookie.categories.includes('targeting') ? 'granted' : 'denied',
      'ad_user_data': cookie.categories.includes('targeting') ? 'granted' : 'denied',
      'ad_personalization': cookie.categories.includes('targeting') ? 'granted' : 'denied',
      'analytics_storage': cookie.categories.includes('analytics') ? 'granted' : 'denied',
      'functionality_storage': cookie.categories.includes('functionality') ? 'granted' : 'denied',
      'personalization_storage': cookie.categories.includes('functionality') ? 'granted' : 'denied',
      'security_storage': cookie.categories.includes('functionality') ? 'granted' : 'denied',
    });
  }
}

export function useCookieConsent() {
  return inject(CookieConsentInjectionKey)!;
}

export function createCookieConsent(locale: string) {
  return {
    install(app: App) {
      const gtm = resolveFromApp(app, GoogleTagManagerInjectionKey);
      const router = resolveFromApp(app, RouterInjectionKey);

      const cookies = new CookieConsent(locale, gtm, router);

      app.provide(CookieConsentInjectionKey, cookies);
    },
  };
}
